import type { ParameterTimeSeriesConfiguration } from "@/lib/api";
import {
  getTimeSeriesParameterEntries,
  type TimeSeriesParameterEntry,
} from "@/features/shared/widgets/observations-map/parameter-display-group-label";

import {
  createDisplayGroupFormId,
  type ObservationsMapDisplayGroupFormEntry,
} from "./display-group-form.utils";

// Label helpers are shared with the read-only dashboard renderer; re-exported here so
// the CMS board consumers keep a single import surface for display-group utilities.
export type { TimeSeriesParameterEntry };
export {
  deriveDisplayGroupLabelFromParameters,
  getTimeSeriesParameterEntries,
  isDisplayGroupLabelOverridden,
  resolveDisplayGroupLabel,
} from "@/features/shared/widgets/observations-map/parameter-display-group-label";

/** Prunes parameters removed from time series. Does not bootstrap or recreate groups. */
export function syncDisplayGroupsFromTimeSeries(
  existing: ObservationsMapDisplayGroupFormEntry[],
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): ObservationsMapDisplayGroupFormEntry[] {
  const entries = getTimeSeriesParameterEntries(timeSeriesConfigurations);
  if (entries.length === 0) return [];

  const requiredIds = new Set(entries.map((e) => e.parameterId));
  if (existing.length === 0) return [];

  const next: ObservationsMapDisplayGroupFormEntry[] = [];
  for (const group of existing) {
    const parameter_ids = group.parameter_ids.filter((id) => requiredIds.has(id));
    if (parameter_ids.length === 0) continue;
    next.push({ ...group, parameter_ids });
  }

  return next.filter((g) => g.parameter_ids.length > 0);
}

function allSourceParametersAreInTarget(
  source: ObservationsMapDisplayGroupFormEntry,
  target: ObservationsMapDisplayGroupFormEntry,
): boolean {
  return source.parameter_ids.every((id) => target.parameter_ids.includes(id));
}

export function resolveDisplayGroupParameterEntry(
  parameterId: string,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
  catalogLabel: (parameterId: string) => string | undefined,
  poolEntries: TimeSeriesParameterEntry[] = [],
): TimeSeriesParameterEntry | undefined {
  const fromTimeSeries = getTimeSeriesParameterEntries(timeSeriesConfigurations).find(
    (entry) => entry.parameterId === parameterId,
  );
  if (fromTimeSeries) return fromTimeSeries;

  const fromPool = poolEntries.find((entry) => entry.parameterId === parameterId);
  if (fromPool) return fromPool;

  const catalog = catalogLabel(parameterId)?.trim();
  if (catalog) return { parameterId, label: catalog };

  return undefined;
}

export function createSingletonDisplayGroupForParameter(
  parameterId: string,
): ObservationsMapDisplayGroupFormEntry {
  return {
    label_override: null,
    parameter_ids: [parameterId],
    id: createDisplayGroupFormId(),
  };
}

export function insertSingletonDisplayGroupFromPool(
  groups: ObservationsMapDisplayGroupFormEntry[],
  parameterId: string,
  atIndex: number,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
  poolEntries: TimeSeriesParameterEntry[] = [],
): ObservationsMapDisplayGroupFormEntry[] {
  const entry = resolveDisplayGroupParameterEntry(
    parameterId,
    timeSeriesConfigurations,
    () => undefined,
    poolEntries,
  );
  if (!entry) return groups;

  const singleton = createSingletonDisplayGroupForParameter(parameterId);
  const index = Math.max(0, Math.min(atIndex, groups.length));
  return [...groups.slice(0, index), singleton, ...groups.slice(index)];
}

export function joinParameterToDisplayGroup(
  groups: ObservationsMapDisplayGroupFormEntry[],
  groupIndex: number,
  parameterId: string,
): ObservationsMapDisplayGroupFormEntry[] {
  const group = groups[groupIndex];
  if (!group || group.parameter_ids.includes(parameterId)) return groups;

  const updated: ObservationsMapDisplayGroupFormEntry = {
    ...group,
    parameter_ids: [...group.parameter_ids, parameterId],
  };
  return groups.map((g, i) => (i === groupIndex ? updated : g));
}

function mergedLabelOverride(
  target: ObservationsMapDisplayGroupFormEntry,
  source: ObservationsMapDisplayGroupFormEntry,
): string | null {
  if (target.label_override?.trim()) return target.label_override;
  if (source.label_override?.trim()) return source.label_override;
  return null;
}

export function mergeDisplayGroups(
  groups: ObservationsMapDisplayGroupFormEntry[],
  fromIndex: number,
  toIndex: number,
): ObservationsMapDisplayGroupFormEntry[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return groups;
  if (fromIndex >= groups.length || toIndex >= groups.length) return groups;

  const source = groups[fromIndex];
  const target = groups[toIndex];
  if (allSourceParametersAreInTarget(source, target)) return groups;

  const mergedIds = [
    ...target.parameter_ids,
    ...source.parameter_ids.filter((id) => !target.parameter_ids.includes(id)),
  ];

  const merged: ObservationsMapDisplayGroupFormEntry = {
    ...target,
    label_override: mergedLabelOverride(target, source),
    parameter_ids: mergedIds,
  };

  const withoutSource = groups.filter((_, i) => i !== fromIndex);
  const adjustedTargetIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
  return withoutSource.map((group, i) => (i === adjustedTargetIndex ? merged : group));
}

export function removeParameterFromDisplayGroup(
  groups: ObservationsMapDisplayGroupFormEntry[],
  groupIndex: number,
  parameterId: string,
): ObservationsMapDisplayGroupFormEntry[] {
  const group = groups[groupIndex];
  if (!group) return groups;

  const parameter_ids = group.parameter_ids.filter((id) => id !== parameterId);
  if (parameter_ids.length === 0) {
    return groups.filter((_, i) => i !== groupIndex);
  }

  return groups.map((g, i) => (i === groupIndex ? { ...g, parameter_ids } : g));
}

export function reorderDisplayGroupParameters(
  groups: ObservationsMapDisplayGroupFormEntry[],
  groupIndex: number,
  fromIndex: number,
  toIndex: number,
): ObservationsMapDisplayGroupFormEntry[] {
  const group = groups[groupIndex];
  if (!group) return groups;

  const parameter_ids = [...group.parameter_ids];
  const [moved] = parameter_ids.splice(fromIndex, 1);
  if (!moved) return groups;
  parameter_ids.splice(toIndex, 0, moved);

  return groups.map((g, i) => (i === groupIndex ? { ...g, parameter_ids } : g));
}

export function displayGroupsEqual(
  a: ObservationsMapDisplayGroupFormEntry[],
  b: ObservationsMapDisplayGroupFormEntry[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (group, index) =>
      group.label_override === b[index].label_override &&
      group.parameter_ids.length === b[index].parameter_ids.length &&
      group.parameter_ids.every((id, i) => id === b[index].parameter_ids[i]),
  );
}
